import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ThemeFontKey, ThemeTokens } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { tenancyApi } from '@/services/tenancy.api';

const FONT_LINK_ID = 'theme-font-link';

/** Google Fonts family name for each curated key — `system` loads nothing,
 *  it just falls through to `--font-sans`. */
const GOOGLE_FONT_FAMILY: Partial<Record<ThemeFontKey, string>> = {
  inter: 'Inter',
  poppins: 'Poppins',
  roboto: 'Roboto',
  'open-sans': 'Open Sans',
  lato: 'Lato',
  nunito: 'Nunito',
  'work-sans': 'Work Sans',
  'source-sans': 'Source Sans 3',
  merriweather: 'Merriweather',
};

const TOKEN_TO_CSS_VAR: Record<Exclude<keyof ThemeTokens, 'fontFamily'>, string> = {
  headerBg: '--theme-header-bg',
  sidebarBg: '--theme-sidebar-bg',
  footerBg: '--theme-footer-bg',
  primaryColor: '--theme-primary',
  secondaryColor: '--theme-secondary',
  tertiaryColor: '--theme-tertiary',
  // Overrides the design system's own `--color-text` directly, rather than
  // a parallel `--theme-text` variable only one rule would have to remember
  // to consult — every one of the ~80 existing `color: var(--color-text)`
  // rules across the app picks this up for free, light or dark mode alike
  // (an inline style here always wins over a stylesheet's `:root` rule).
  textColor: '--color-text',
};

function loadGoogleFont(family: string): void {
  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function removeGoogleFont(): void {
  document.getElementById(FONT_LINK_ID)?.remove();
}

/**
 * Applies a theme's tokens to the live document — the single function both
 * `ThemeProvider` (the real active theme, on session load) and the
 * appearance editor's live preview (a draft, before saving) call, so
 * "preview" is never a separate mockup, it's the real shell reacting.
 *
 * `tokens: null` clears every custom property back to the app's own
 * defaults — used on logout/tenant-switch so one church's brand colors
 * never bleed onto the next login screen or the next tenant in the same
 * browser session.
 */
export function applyTheme(tokens: ThemeTokens | null): void {
  const root = document.documentElement.style;

  if (!tokens) {
    for (const cssVar of Object.values(TOKEN_TO_CSS_VAR)) root.removeProperty(cssVar);
    root.removeProperty('--theme-font');
    removeGoogleFont();
    return;
  }

  for (const [key, cssVar] of Object.entries(TOKEN_TO_CSS_VAR) as [
    Exclude<keyof ThemeTokens, 'fontFamily'>,
    string,
  ][]) {
    root.setProperty(cssVar, tokens[key]);
  }

  const family = GOOGLE_FONT_FAMILY[tokens.fontFamily];
  if (family) {
    root.setProperty('--theme-font', `"${family}", var(--font-sans)`);
    loadGoogleFont(family);
  } else {
    root.removeProperty('--theme-font');
    removeGoogleFont();
  }
}

/** Fetches this tenant's active theme once per session and applies it to
 *  the real document — mounted inside `AppShell` only, never in `main.tsx`,
 *  so login/register/the public giving page stay on the app's own default
 *  look. */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const { tenantId } = useAuth();

  const activeTheme = useQuery({
    queryKey: ['tenancy', 'themes', 'active', tenantId],
    queryFn: ({ signal }) => tenancyApi.getActiveTheme(signal),
    enabled: Boolean(tenantId),
  });

  useEffect(() => {
    if (activeTheme.data) applyTheme(activeTheme.data.tokens);
    return () => applyTheme(null);
  }, [activeTheme.data]);

  return children;
}
